import { useEffect, useRef } from 'react'

const vertexShaderSource = `
  attribute vec2 a_position;
  varying vec2 v_uv;

  void main() {
    v_uv = a_position * .5 + .5;
    gl_Position = vec4(a_position, 0., 1.);
  }
`

const fragmentShaderSource = `
  precision mediump float;

  varying vec2 v_uv;
  uniform vec2 u_resolution;
  uniform vec2 u_pointer;
  uniform float u_time;

  float hash(vec2 value) {
    value = fract(value * vec2(123.34, 456.21));
    value += dot(value, value + 45.32);
    return fract(value.x * value.y);
  }

  float particles(vec2 point, float scale, float drift) {
    vec2 space = point * scale;
    vec2 cell = floor(space);
    vec2 local = fract(space) - .5;
    float seed = hash(cell);
    vec2 offset = vec2(hash(cell + 8.7), hash(cell + 31.4)) - .5;
    offset.x += sin(u_time * drift + seed * 6.283) * .08;
    float twinkle = .5 + .5 * sin(u_time * (1.35 + seed * 1.8) + seed * 18.);
    float angle = seed * 1.2 - .6;
    mat2 rotation = mat2(cos(angle), -sin(angle), sin(angle), cos(angle));
    vec2 delta = rotation * (local - offset * .74);
    delta /= mix(.72, 1.18, twinkle);
    float verticalRay = length(delta * vec2(7.8, 1.));
    float horizontalRay = length(delta * vec2(1., 7.8));
    float pointedStar = smoothstep(.07, .006, min(verticalRay, horizontalRay));
    float brightCore = smoothstep(.025, .002, length(delta));
    return (pointedStar * .72 + brightCore) * step(.72, seed) * (.3 + .7 * twinkle);
  }

  void main() {
    vec2 uv = v_uv;
    vec2 point = uv - .5;
    point.x *= u_resolution.x / max(u_resolution.y, 1.);

    vec2 pointer = (u_pointer - .5) * vec2(u_resolution.x / max(u_resolution.y, 1.), 1.);
    float pointerGlow = exp(-3.8 * length(point - pointer)) * .12;

    float stars = particles(point + vec2(u_time * .006, 0.), 10., .35);
    stars += particles(point - vec2(u_time * .004, u_time * .002), 17., .22) * .55;

    vec2 orbitCenter = vec2(.56, .25);
    float orbit = smoothstep(.012, .0015, abs(length(point - orbitCenter) - .39));
    float orbitWide = smoothstep(.009, .001, abs(length(point - orbitCenter) - .56));
    float sweep = .42 + .58 * sin(atan(point.y - orbitCenter.y, point.x - orbitCenter.x) * 2. - u_time * .34);

    vec2 gridPoint = point;
    gridPoint.y += .58;
    float depth = max(abs(gridPoint.y), .055);
    vec2 perspective = vec2(gridPoint.x / depth, 1. / depth + u_time * .18);
    vec2 gridDistance = abs(fract(perspective * vec2(1.5, .12)) - .5);
    vec2 gridLine = 1. - smoothstep(vec2(.455), vec2(.495), gridDistance);
    float grid = max(gridLine.x, gridLine.y);
    grid *= smoothstep(.08, .7, uv.y) * smoothstep(1., .42, uv.y) * .055;

    vec3 blue = vec3(.22, .52, 1.);
    vec3 cyan = vec3(.22, .78, 1.);
    vec3 color = blue * stars * .7;
    color += mix(blue, cyan, sweep) * orbit * sweep * .16;
    color += blue * orbitWide * .055;
    color += blue * grid;
    color += cyan * pointerGlow;

    float vignette = smoothstep(1.05, .18, length(point));
    float alpha = min(.72, (stars + orbit * .22 + orbitWide * .08 + grid + pointerGlow) * vignette);
    gl_FragColor = vec4(color, alpha);
  }
`

function createShader(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type)
  if (!shader) return null
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader)
    return null
  }
  return shader
}

export function LoginWebGLBackdrop() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const gl = canvas.getContext('webgl', { alpha: true, antialias: false, powerPreference: 'low-power' })
    if (!gl) return

    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource)
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource)
    const program = gl.createProgram()
    if (!vertexShader || !fragmentShader || !program) return
    gl.attachShader(program, vertexShader)
    gl.attachShader(program, fragmentShader)
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return

    const positionBuffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW)
    const positionLocation = gl.getAttribLocation(program, 'a_position')
    const resolutionLocation = gl.getUniformLocation(program, 'u_resolution')
    const pointerLocation = gl.getUniformLocation(program, 'u_pointer')
    const timeLocation = gl.getUniformLocation(program, 'u_time')
    gl.useProgram(program)
    gl.enableVertexAttribArray(positionLocation)
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0)
    gl.enable(gl.BLEND)
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE)

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const pointer = { x: .7, y: .25, targetX: .7, targetY: .25 }
    let animationFrame = 0
    let startTime = performance.now()

    const resize = () => {
      const bounds = canvas.getBoundingClientRect()
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5)
      canvas.width = Math.max(1, Math.round(bounds.width * pixelRatio))
      canvas.height = Math.max(1, Math.round(bounds.height * pixelRatio))
      gl.viewport(0, 0, canvas.width, canvas.height)
    }

    const handlePointer = (event: PointerEvent) => {
      const bounds = canvas.getBoundingClientRect()
      pointer.targetX = (event.clientX - bounds.left) / bounds.width
      pointer.targetY = 1 - (event.clientY - bounds.top) / bounds.height
    }

    const render = (now: number) => {
      pointer.x += (pointer.targetX - pointer.x) * .045
      pointer.y += (pointer.targetY - pointer.y) * .045
      gl.uniform2f(resolutionLocation, canvas.width, canvas.height)
      gl.uniform2f(pointerLocation, pointer.x, pointer.y)
      gl.uniform1f(timeLocation, (now - startTime) / 1000)
      gl.clearColor(0, 0, 0, 0)
      gl.clear(gl.COLOR_BUFFER_BIT)
      gl.drawArrays(gl.TRIANGLES, 0, 3)
      if (!reducedMotion) animationFrame = requestAnimationFrame(render)
    }

    resize()
    const resizeObserver = new ResizeObserver(resize)
    resizeObserver.observe(canvas)
    canvas.parentElement?.addEventListener('pointermove', handlePointer)
    render(startTime)

    return () => {
      cancelAnimationFrame(animationFrame)
      resizeObserver.disconnect()
      canvas.parentElement?.removeEventListener('pointermove', handlePointer)
      gl.deleteBuffer(positionBuffer)
      gl.deleteProgram(program)
      gl.deleteShader(vertexShader)
      gl.deleteShader(fragmentShader)
    }
  }, [])

  return <canvas ref={canvasRef} className="login-webgl" aria-hidden="true"/>
}
